import { useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import api from "../../services/api";
import { schedulePayload } from "../../pages/Schedules/scheduleUtils";

export default function useScheduleSubmit({ user, scheduleId, prepared = false, onSaved }) {
  const [submitting, setSubmitting] = useState(false), [error, setError] = useState(""), [lockedDraft, setLockedDraft] = useState(false);
  const [partialScheduleId, setPartialScheduleId] = useState(scheduleId || null);
  const live = useRef(true), busy = useRef(false);
  const saved = useRef({ id: scheduleId || null, created: false, payload: null, serialized: null, uploaded: false });
  useEffect(() => () => { live.current = false; }, []);
  const update = fn => { if (live.current) fn(); };
  const begin = () => { if (busy.current) return false; busy.current = true; update(() => { setSubmitting(true); setError(""); }); return true; };
  const end = () => { busy.current = false; update(() => setSubmitting(false)); };

  async function submit(values, media) {
    if (!begin()) return null;
    let stage = "save";
    try {
      const payload = schedulePayload(values, user.id), serialized = JSON.stringify(payload), intent = saved.current;
      if (!scheduleId && intent.payload && intent.serialized !== serialized) throw new Error("DRAFT_LOCKED");
      if (!intent.id) {
        if (!intent.payload) { intent.payload = { ...payload, requestId: uuid(), prepare: true, hasMedia: Boolean(media) }; intent.serialized = serialized; }
        const { data } = await api.post("/schedules", intent.payload);
        if (!data?.id) throw new Error("INVALID_SAVE_RESPONSE");
        intent.id = data.id; intent.created = true;
        update(() => setPartialScheduleId(data.id));
      } else if (scheduleId && intent.serialized !== serialized) {
        await api.put(`/schedules/${intent.id}`, payload); intent.serialized = serialized;
      }
      if (media && !intent.uploaded) {
        stage = "upload";
        const file = new FormData(); file.append("file", media);
        await api.post(`/schedules/${intent.id}/media-upload`, file); intent.uploaded = true;
      }
      if (intent.created || prepared) { stage = "activate"; await api.post(`/schedules/${intent.id}/activate`); }
      const result = { id: intent.id, sendAt: payload.sendAt };
      update(() => { setLockedDraft(false); if (onSaved) onSaved(result); });
      return result;
    } catch (err) {
      const intent = saved.current;
      // Keep a lost creation response on the same key. Preparation cannot send
      // anything until activation, and activation always has a known record id.
      if (!intent.id && err.response && err.response.status < 500 && err.response.status !== 409) { intent.payload = null; intent.serialized = null; }
      update(() => {
        setLockedDraft(Boolean(intent.payload));
        setError(err.message === "DRAFT_LOCKED" ? "Conclua ou cancele a tentativa anterior antes de alterar os dados." : stage === "upload" ? "O rascunho do agendamento foi salvo, mas o anexo não foi concluído. Tente salvar novamente para reenviar a imagem." : stage === "activate" ? "Não foi possível confirmar a ativação. Tente novamente ou cancele o agendamento." : "Não foi possível salvar. Seus dados foram mantidos para tentar novamente.");
      });
      return null;
    } finally { end(); }
  }

  async function cancel() {
    if (!begin()) return false;
    try {
      const intent = saved.current;
      if (!scheduleId && intent.payload) {
        if (!intent.id) { const { data } = await api.post("/schedules", intent.payload); if (!data?.id) throw new Error("INVALID_SAVE_RESPONSE"); intent.id = data.id; }
        await api.delete(`/schedules/${intent.id}`);
      }
      return true;
    } catch (_) {
      update(() => setError("Não foi possível confirmar o cancelamento. Tente novamente; seus dados foram mantidos."));
      return false;
    } finally { end(); }
  }
  return { submit, cancel, submitting, error, lockedDraft, partialScheduleId, canReplaceMedia: Boolean(saved.current.payload?.hasMedia && saved.current.id && !saved.current.uploaded) };
}
